import { generateId } from '@adp-wfn/mdf-core';
import { isEqual } from 'lodash';
import React from 'react';
import { Overlay2 } from './Overlay2';

import { IMDFFieldState } from './FieldState';
import { ValidationContainerContext } from './MDFContext';
import { MDFLabel } from './MDFLabel';
import { MDFPopover } from './MDFPopover';
import logger from '../util/logger';

export interface IValidatedState {
  isDisabled: boolean;
  isReadOnly: boolean;
  isRequired: boolean;
  isTouched: boolean;
  isValid: boolean;
  message: string;
  value: any;
}

export function MDFValidatedField(Field) {
  return class extends React.Component<any, IMDFFieldState> {
    static displayName = `MDFValidated${Field.displayName ?? 'Field'}`;

    private field: any;
    private name: string;
    private touched = false;
    private validatedState: IValidatedState;
    private validatedFieldRef = React.createRef<HTMLDivElement>();

    declare context: React.ContextType<typeof ValidationContainerContext>;
    static contextType = ValidationContainerContext;

    state: IMDFFieldState = {
      value: null,
      isValid: true,
      validationMessage: '',
      showPopover: false
    };

    validationObject = {
      errorId: 'errorID_' + this.props.id,
      labelClass: ' mdf-label-error',
      fieldClass: ' vdl-validation-error',
      ariaInvalid: true
    };

    constructor(props: any) {
      super(props);

      if (!props.name) {
        console.warn('Validated fields must have a name attribute! Using an autogenerated name instead.');
      }

      this.name = props.name || generateId('MDFValidatedFieldName');

      let initialValue = props.value;

      if (Field.displayName) {
        const componentPropValue = this.componentHasOwnProperty(props);

        initialValue = componentPropValue !== null ? componentPropValue : initialValue;
      }

      const validFields = this.validate(initialValue, false, null, null);

      this.state = {
        value: initialValue,
        isValid: props.isValid === false ? props.isValid : validFields.isValid,
        validationMessage: props.serverMessage || validFields.validationMessage,
        showPopover: false
      };

      this.validatedState = {
        isDisabled: this.props.disabled || false,
        isReadOnly: this.props.readOnly || false,
        isRequired: this.props.required || false,
        isTouched: this.touched,
        isValid: this.state.isValid,
        message: this.state.validationMessage,
        value: this.state.value
      };
    }

    componentDidMount() {
      if (this.context.registerField) {
        // Attaching the component to the form
        this.context.registerField(this);
      }
    }

    componentWillUnmount() {
      if (this.context.unregisterField) {
        // Detaching if unmounting
        this.context.unregisterField(this);
      }
    }

    componentDidUpdate(prevProps) {
      // Check if props have changed by comparing values
      let currentValue = this.props.value;
      let prevValue = prevProps.value;

      // Extract component-specific values for comparison
      if (Field.displayName) {
        const getCurrentComponentValue = () => {
          if (Field.displayName.includes('Radio')) {
            return this.props.radioGroupValue;
          }

          if (Field.displayName.includes('DatePicker')) {
            return this.props.selectedDate;
          }

          if (Field.displayName.includes('DateRangePicker')) {
            return { startDate: this.props.startDate, endDate: this.props.endDate };
          }

          if (Field.displayName.includes('Phone')) {
            return this.props.phone;
          }

          if (Field.displayName.includes('DualMulti')) {
            return this.props.selected;
          }

          if (Field.displayName.includes('CheckBox') || Field.displayName.includes('ToggleSwitch')) {
            return this.props.checked;
          }

          return null;
        };

        const getPrevComponentValue = () => {
          if (Field.displayName.includes('Radio')) {
            return prevProps.radioGroupValue;
          }

          if (Field.displayName.includes('DatePicker')) {
            return prevProps.selectedDate;
          }

          if (Field.displayName.includes('DateRangePicker')) {
            return { startDate: prevProps.startDate, endDate: prevProps.endDate };
          }

          if (Field.displayName.includes('Phone')) {
            return prevProps.phone;
          }

          if (Field.displayName.includes('DualMulti')) {
            return prevProps.selected;
          }

          if (Field.displayName.includes('CheckBox') || Field.displayName.includes('ToggleSwitch')) {
            return prevProps.checked;
          }

          return null;
        };

        const currentComponentValue = getCurrentComponentValue();
        const prevComponentValue = getPrevComponentValue();

        currentValue = currentComponentValue !== null ? currentComponentValue : currentValue;
        prevValue = prevComponentValue !== null ? prevComponentValue : prevValue;
      }

      const propsHasChanged = !isEqual(currentValue, prevValue);

      // Treat undefined incoming value as uncontrolled when we already hold a defined value
      const hasDefinedStateValue = this.state.value !== null && this.state.value !== undefined && this.state.value !== '';
      // Treat empty string like undefined for NumberBox skip when we already have a non-empty state value.
      const propIsEmptyLike = this.props.value === undefined || this.props.value === '';
      const skipPropUpdate = Field.displayName.includes('NumberBox') && propIsEmptyLike && hasDefinedStateValue;

      if (!skipPropUpdate && propsHasChanged) {
        // Check to ensure we don't validate with stale state
        const stateValue = this.state.value;
        const shouldUseCurrentValue = !isEqual(currentValue, stateValue);

        // Perform the validation and field update that was in componentWillReceiveProps
        // Use the current prop value if state is stale, otherwise use state value
        this.updateField(
          shouldUseCurrentValue ? currentValue : stateValue,
          this.props.validateOnRender,
          this.field?.isValid,
          this.field?.validationMessage,
          this.state.showPopover
        );
      }

      // If the component has isValidComponent method, get validation info from component and
      // update the validated field state if there is mismatch.
      if (this.field?.isValidComponent) {
        let validData = this.field.isValidComponent();

        validData.isValid = (validData.isValid !== false);

        if (validData && validData.isValid && this.props.onValidate) {
          validData = this.props.onValidate(validData);
        }

        // Only update if there's an actual change to prevent unnecessary re-validation
        if (validData && validData.isValid !== this.state.isValid && validData.value !== null && validData.value !== undefined) {
          this.updateField(validData.value, this.field.props.validateOnRender, validData.isValid, validData.validationMessage);
        }
      }

      if (this.context.updateFieldMeta) {
        this.context.updateFieldMeta(this);
      }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
      let nextValue = nextProps.value;

      // When the component does not use "value" for props we need to update the nextValue to its appropriate value.
      if (Field.displayName) {
        // Extract component-specific prop values inline
        let componentPropValue = null;

        if (Field.displayName.includes('Radio')) {
          // Use the radioGroupValue as the value if the Field is a Radio component.
          componentPropValue = nextProps.radioGroupValue;
        }
        else if (Field.displayName.includes('DatePicker')) {
          // Use the selectedDate as the value for DatePicker component
          componentPropValue = nextProps.selectedDate;
        }
        else if (Field.displayName.includes('DateRangePicker')) {
          // Use the startDate and endDate as the value for DateRangePicker component
          componentPropValue = { startDate: nextProps.startDate, endDate: nextProps.endDate };
        }
        else if (Field.displayName.includes('Phone')) {
          // Use the Phone as the value for Phone component
          componentPropValue = nextProps.phone;
        }
        else if (Field.displayName.includes('DualMulti')) {
          // Use the selected as the value for DualMulti component
          componentPropValue = nextProps.selected;
        }
        else if (Field.displayName.includes('CheckBox') || Field.displayName.includes('ToggleSwitch')) {
          // Use the checked value for toggle switch and checkbox component
          componentPropValue = nextProps.checked;
        }

        // for Checkbox and ToggleButton the checked property is used, when it gets unchecked the componentPropValue returns a false
        // changed the condition to check if componentPropValue is not null then use the componentPropValue
        nextValue = componentPropValue !== null ? componentPropValue : nextValue;
      }
      else {
        console.error('MDFValidatedField.getDerivedStateFromProps(): Field does not have a displayName.', Field.displayName);
      }

      // Ignore undefined OR empty-string prop regression for NumberBox when we already hold a non-empty value.
      if (Field.displayName.includes('NumberBox') && (nextProps.value === undefined || nextProps.value === '') && prevState.value !== null && prevState.value !== undefined && prevState.value !== '') {
        return null;
      }

      const propsHasChanged = !isEqual(nextValue, prevState.value);

      // When a new value comes from the app's properties, return the new state
      if (propsHasChanged) {
        return {
          value: nextValue,
          isValid: prevState.isValid,
          validationMessage: prevState.validationMessage,
          showPopover: prevState.showPopover
        };
      }

      // No state update needed
      return null;
    }

    componentHasOwnProperty(props) {
      logger('MDFValidatedField.componentHasOwnProperty(): Entering. Field =', Field.displayName);

      let initialValue = null;

      // Use the radioGroupValue as the value if the Field is a Radio component.
      // The only way we can tell (at the moment) is by inspecting the display name.
      if (Field.displayName.includes('Radio')) {
        initialValue = props.radioGroupValue;
      }

      // Use the selectedDate as the value for DatePicker component
      if (Field.displayName.includes('DatePicker')) {
        initialValue = props.selectedDate;
      }

      // Use the startDate and endDate as the value for DateRangePicker component
      if (Field.displayName.includes('DateRangePicker')) {
        initialValue = { startDate: props.startDate, endDate: props.endDate };
      }

      // Uses the Phone as the value for Phone component
      if (Field.displayName.includes('Phone')) {
        initialValue = props.phone;
      }

      // Uses the selected as the value for DualMulti component
      if (Field.displayName.includes('DualMulti')) {
        initialValue = props.selected;
      }

      // Use the checked value for toggleswitch and checkbox component
      if (Field.displayName.includes('CheckBox') || Field.displayName.includes('ToggleSwitch')) {
        initialValue = props.checked;
      }

      logger('MDFValidatedField.componentHasOwnProperty(): value =', initialValue);

      return initialValue;
    }

    shouldComponentUpdate(nextProps, nextState) {
      let nextValue = nextProps.value;

      // When the component does not use "value" for props we need to update the nextValue to its appropriate value.
      if (Field.displayName) {
        const componentPropValue = this.componentHasOwnProperty(nextProps);

        // for Checkbox and ToggleButton the checked property is used, when it gets unchecked the componentPropValue returns a false
        // changed the condition to check if componentPropValue is not null then use the componentPropValue
        nextValue = componentPropValue !== null ? componentPropValue : nextValue;
      }

      // WFNMDFP-221: ValidatedField is rerendering irrespective of props change when wrapped with ValidationContainer due to React Context behaviour(ValidationContainerContext).
      // Skip ValidatedField rerender if nextProps value is not in sync with latest state value (user updated value is not yet considered by react-redux).
      if (!isEqual(nextValue, nextState.value)) {
        return false;
      }

      return true;
    }

    private setField = (field: any) => {
      this.field = field;

      if (this.field) {
        if (this.field.props?.validateOnRender && (this.state.isValid === false)) {
          this.updateField(this.state.value, true, this.state.isValid, this.state.validationMessage);
        }

        // Check to see if the component has isValidComponent method
        if (this.field.isValidComponent) {
          const validData = this.field.isValidComponent();

          // updateField only when component updates the isValid property to false
          if (validData && validData.isValid === false) {
            this.updateField(validData.value, this.field.props.validateOnRender, validData.isValid, validData.validationMessage);
          }
        }
      }
    };

    onFocus = (e) => {
      // Pass through focus events.
      if (!this.props.disabled) {
        this.props.onFocus?.(e);
        this.setState({ showPopover: true }, () => {
          /* DE583630 Fix - When validation container has set of validated components in which some components (from adp-react end) are following focus-mixin async way to fire focus/blur events (this.props.blur()) and
          * other components are firing focus/blur events synchronously. In general, when there is a change in focus, MDFValidatedField component’s blur event must execute first and then focus.
          * But because of mentioned mismatch when control goes from focus-mixin async patterned components to other component which fires events synchronously, focus is executing first followed by blur (it is still in event queue).
          * Focused component is already in touched state so when blur event executes, already touched component will be updated. On component update, "is required" text is showing on the focused one that is not the expected behavior.
          * Now moved setting touched property logic to Event Table through setTimeout. so, it will maintain order in Event Queue.
          */
          setTimeout(() => {
            this.touched = true;
          }, 0);
        });
      }
    };

    onBlur = (e) => {
      // Validate the current state to catch tab-throughs
      // in addition to 'normal' blur events when the field is not disabled.
      if (!this.props.disabled) {
        this.updateField(this.state.value, true, this.state.isValid, this.state.validationMessage);
        this.props.onBlur?.(e, this.validatedState);
        this.setState({ showPopover: false });
      }
    };

    onChange = (value, isValid, validationMessage, otherParams = {}) => {
      // All value changes must flow through here. Field components
      // need to signal all value changes through an onChange() handler.
      // listen to any isValid and validationMessages from other components and pass it to validate function when the field is not disabled.

      // When usePopover is set to true - wait for setState to finish, before triggering updateField function. Moving updateField as a callback function inside the setState
      // Close the popover onBlur/onChange when immediate property is not set.
      // Normalize clears for Date/Time pickers so state shows an explicit null (drives child props below)
      if (!this.props.disabled) {
        if ((Field.displayName.includes('DatePicker') || Field.displayName.includes('TimePicker')) && (value === '' || value === undefined)) {
          value = null;
        }

        this.updateField(value, true, isValid, validationMessage, this.props.usePopover && this.props.immediate, otherParams);
        this.props.onChange?.(value, this.validatedState, otherParams);
      }
    };

    onDropDownMenuChange = (index, value) => {
      this.onChange(value, null, null, { index });
    };

    onPhoneChange = (value, isValid, validationMessage, otherParams) => {
      logger('MDFValidatedField.onPhoneChange(): Entering. value = ', value);

      if (!this.state.value && typeof value === 'object' && value.phone === '') {
        // When the initial value is empty and the user tabs through the phone number
        // field, the phone number will send back a value object in its onBlur event
        // that is an object with an empty phone field. We need to signal back that
        // the value is still empty.
        this.onChange(this.state.value, isValid, validationMessage, otherParams);
      }
      else {
        // The value likely already changed once, so report back the new value.
        this.onChange(value, isValid, validationMessage, otherParams);
      }
    };

    DateRangeChange = (value, isValid, validationMessage, otherParams = {}) => {
      if (!this.props.disabled) {
        this.updateField(value, true, isValid, validationMessage, this.props.usePopover && this.props.immediate, otherParams);
        this.props.onRangeChange?.(value, this.validatedState, otherParams);
      }
    };

    reset = () => {
      this.touched = false;
      this.setState({ showPopover: false });
    };

    updateField = (value, showValidation, isValid = null, validationMessage = null, showPopover?: boolean, otherParams?: object) => {
      const validState = this.validate(value, showValidation, isValid, validationMessage);
      const params = otherParams ? ((typeof otherParams === 'object') ? otherParams : { otherParams: otherParams }) : null;

      const newValidState = Object.assign({}, { ...validState, ...params }, { showPopover: showPopover });
      this.updateState(newValidState);

      if (this.context.updateFieldState) {
        this.context.updateFieldState(this.name, { ...validState, ...params });
      }
    };

    onComponentValidate = (isValid, validationMessage) => {
      this.setState({ isValid: isValid, validationMessage: validationMessage });
    };

    updateState = (validState) => {
      // Update the object we are passing back in onChange and onBlur
      this.validatedState.isTouched = this.touched;
      this.validatedState.value = validState.value;
      this.validatedState.isValid = validState.isValid;
      this.validatedState.message = validState.validationMessage;

      this.setState({ ...validState });
    };

    validate = (value, showValidation, isValid, validationMessage, showPopover?): IMDFFieldState => {
      // Validation stops if the isValid is already false
      let validState = { value, isValid: isValid !== false, validationMessage: validationMessage ? validationMessage : null, showPopover: showPopover };

      if (showValidation) {
        this.touched = true;
      }

      if (this.props.onValidate) {
        const result = this.props.onValidate(validState) || {};

        // Previous value overwrite check for NumberBox to avoid impacting other components' intentional value overrides.
        if (Field.displayName.includes('NumberBox')) {
          const previousStateValue = this.state?.value;

          if (previousStateValue !== undefined && previousStateValue !== value && result.value === previousStateValue) {
            result.value = value;
          }
        }

        validState = { ...validState, ...result };
      }

      return validState;
    };

    showValidationPopover = (errorId) => {
      const { isValid } = this.state;
      // default to colored popover - error
      const popoverErrorClass = 'mdf-overlay-popover vdl-popover--error';

      if (this.props.usePopover && !isValid && (this.props.validateMessage || this.state.validationMessage)) {
        return (
          <Overlay2 rootClose={false} show={true} target={this.validatedFieldRef} flip={true} placement="bottom">
            <MDFPopover
              id={errorId + '_validation'}
              errorId={errorId}
              containerClassName={popoverErrorClass}
              title={this.props.popoverTitle && this.props.popoverTitle.trim()}>
              {this.props.validateMessage || this.state.validationMessage}
            </MDFPopover>
          </Overlay2>
        );
      }
    };

    render() {
      // eslint-disable-next-line prefer-const
      let { adpFieldId, 'aria-invalid': ariaInvalid, className, errorId, fieldClassName, hasHtml, helpMessage, id, labelClassName, labelText, name, placement, required, usePopover, onValidate, ...passDownProps } = this.props;

      const { isValid, validationMessage, showPopover } = this.state;

      // Class is added to label to sync styles with synergy oneUX web Components
      if (!isValid && this.touched && !this.props.disabled) {
        labelClassName = labelClassName ? labelClassName + '' + ' mdf-form-control-label-error' : '' + ' mdf-form-control-label-error';
      }
      else {
        labelClassName = labelClassName ? labelClassName : '' + ' mdf-form-control-label';
      }

      if (!isValid && this.touched && !this.props.disabled) {
        errorId = this.validationObject.errorId;
        labelClassName = (labelClassName || '') + this.validationObject.labelClass;
        fieldClassName = (fieldClassName || '') + this.validationObject.fieldClass;
        ariaInvalid = this.validationObject.ariaInvalid;

        // Append the validationMessage to the label only when usePopover is not true.
        if (labelText && !usePopover) {
          labelText += validationMessage;
        }
      }

      let nodeId = id;

      if (!nodeId) {
        nodeId = generateId('mdfValidatedField');
      }

      const labelProps: any = {};

      if (adpFieldId) {
        labelProps.adpFieldId = adpFieldId;
      }

      if (Field.displayName === 'MDFPhone') {
        labelProps.id = generateId('MDFPhone');

        // Added to make MDFPhone accessible when
        // 'labelText' property is provided without 'aria-labelledby' property
        if (!this.props['aria-labelledby'] && labelText) {
          passDownProps = Object.assign(passDownProps, { 'aria-labelledby': labelProps.id });
        }
      }

      let htmlForId;

      if (Field.displayName === 'MDFPhone') {
        htmlForId = '';
      }
      else if (Field.displayName === 'TimePicker') {
        htmlForId = nodeId + '_input';
      }
      else {
        htmlForId = nodeId;
      }

      let fieldOnChange = this.onChange;

      switch (Field.displayName) {
        case 'MDFDropdownMenu':
          fieldOnChange = this.onDropDownMenuChange;
          break;
        case 'MDFPhone':
          fieldOnChange = this.onPhoneChange;
          break;
      }

      // Conditional control helpers
      const isDatePicker = Field.displayName.includes('DatePicker');
      const isTimePicker = Field.displayName.includes('TimePicker');

      // Determine if parent explicitly provided the controlling prop.
      // Using hasOwnProperty so falsy values (null/undefined/empty string) still count as 'provided'.
      const parentSuppliesSelectedDateProp = Object.prototype.hasOwnProperty.call(this.props, 'selectedDate');
      const parentSuppliesTimeValueProp = Object.prototype.hasOwnProperty.call(this.props, 'value');

      // If parent is NOT supplying the prop, we inject wrapper state so clear/repopulate works.
      const pickerProps = !parentSuppliesSelectedDateProp && isDatePicker ? { selectedDate: this.state.value } : !parentSuppliesTimeValueProp && isTimePicker ? { value: this.state.value } : {};

      return (
        <div className={'mdf-validated-field ' + className}>
          {Field.displayName === 'MDFDropdownList'
            ? labelText &&
              <MDFLabel
                {...labelProps}
                className={labelClassName}
                id={`${id}_mdf_dropdownlist`}
                required={required}
                labelText={labelText}
                hasHtml={hasHtml}
                placement={placement || 'top'}
                helpMessage={helpMessage}>
              </MDFLabel>
            : labelText &&
              <MDFLabel
                {...labelProps}
                htmlFor={htmlForId}
                className={labelClassName}
                required={required}
                labelText={labelText}
                hasHtml={hasHtml}
                placement={placement || 'top'}
                helpMessage={helpMessage}>
              </MDFLabel>
          }
          <div ref={this.validatedFieldRef}>
            {Field.displayName === 'DataRangePicker'
              ?
              <Field
                aria-invalid={ariaInvalid}
                {...passDownProps}
                errorId={errorId}
                name={name}
                className={fieldClassName}
                required={required}
                aria-required={required}
                ref={this.setField}
                validatedField={true}
                onRangeChange={this.DateRangeChange}
                id={nodeId}
              />
              :
              <Field
                aria-invalid={ariaInvalid}
                {...passDownProps}
                errorId={errorId}
                name={name}
                className={fieldClassName}
                required={required}
                aria-required={required}
                ref={this.setField}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                validatedField={true}
                onChange={fieldOnChange}
                id={nodeId}
                // NOTE: We intentionally spread a conditional object here for TimePicker and DatePicker based on the condition.
                // Rationale: DatePicker and TimePicker both needs a controlled value for programmatic repopulate.
                // For every other component we avoid adding a value prop so their existing
                // controlled/uncontrolled behavior is preserved and we do NOT trigger React warnings.
                {...pickerProps}
              />
            }
          </div>
          <div aria-live="polite">
            {showPopover &&
              <div>
                {this.showValidationPopover(errorId)}
              </div>
            }
          </div>
        </div>
      );
    }
  };
}
